import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Crown, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const COPY = {
  checking: ["Confirming your payment…", "This only takes a moment."],
  paid: ["Welcome to LoreRight Pro.", "Timelines, Calendars, Maps, Relationships and Languages are now unlocked in all your universes."],
  failed: ["We couldn't confirm the payment.", "If you were charged, it will show up shortly. Otherwise, try again from the plans page."],
  cancel: ["Checkout cancelled.", "No charge was made. You can upgrade any time."],
};

export default function PaymentResultPage({ cancelled = false }) {
  const [params] = useSearchParams();
  const { reload } = useAuth();
  const [state, setState] = useState(cancelled ? "cancel" : "checking");

  useEffect(() => {
    if (cancelled) return;
    const sid = params.get("session_id");
    let tries = 0, stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); reload(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) { if (e.response?.status === 404) { setState("failed"); return; } }
      if (++tries >= 15) setState("failed");
      else setTimeout(poll, 2000);
    };
    if (sid) poll(); else setState("failed");
    return () => { stop = true; };
  }, [cancelled, params, reload]);

  const [title, body] = COPY[state];
  const Icon = state === "checking" ? Loader2 : state === "paid" ? Crown : XCircle;
  return (
    <div className="flex min-h-screen items-center justify-center lf-glow lf-grain p-6" data-testid="payment-result">
      <div className="relative z-10 max-w-lg text-center">
        <Icon className={`mx-auto h-10 w-10 ${state === "paid" ? "text-[#FBBF24]" : "text-[#A8A090]"} ${state === "checking" ? "animate-spin" : ""}`} />
        <h1 className="mt-5 font-display text-4xl text-[#F3EBDD]" data-testid="payment-result-title">{title}</h1>
        <p className="mt-2 text-sm text-[#A8A090]">{body}</p>
        <div className="mt-8 flex justify-center gap-5 text-sm">
          <Link to="/" className="text-[#F59E0B] hover:underline" data-testid="payment-home-link">Back to my universes</Link>
          {state !== "paid" && state !== "checking" && <Link to="/pricing" className="text-[#A8A090] hover:text-[#F3EBDD]" data-testid="payment-pricing-link">View plans</Link>}
        </div>
      </div>
    </div>
  );
}
